import { Component, OnInit, Inject, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material';
import { ToastrService } from 'ngx-toastr';
import { AuthService } from '../../services/auth.service';
import { PaymentService } from '../../services/payment.service';
import { TicketDialogService } from '../ticket-dialog/ticket-dialog.service';
import { User } from '../../../pages/users/user.model';
import * as moment from 'moment';
import { environment } from '../../../../environments/environment';

declare var window: any;

type DialogState = 'loading' | 'checkout' | 'confirm_subscription' | 'success' | 'error';

@Component({
    selector: 'app-conekta-subscription-dialog',
    templateUrl: './conekta-subscription-dialog.component.html',
    styleUrls: ['./conekta-subscription-dialog.component.scss'],
    providers: [PaymentService, TicketDialogService]
})
export class ConektaSubscriptionDialogComponent implements OnInit, OnDestroy {

    public state: DialogState = 'loading';
    public user: User;
    public cxc: any;
    public errorMessage: string = '';

    private checkoutId: string = '';
    private customerId: string = '';
    public orderId: string = '';        // público: usado en el template
    private transaction: string = '';
    private conektaScript: HTMLScriptElement;
    private saving: boolean = false;
    private destroyed: boolean = false;
    public paymentSubmitted: boolean = false;
    public showPolicy: boolean = false;
    private messageHandler: (e: MessageEvent) => void;

    constructor(
        public dialogRef: MatDialogRef<ConektaSubscriptionDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: any,
        private conektaService: PaymentService,
        private ticketService: TicketDialogService,
        private toastr: ToastrService,
        private cdr: ChangeDetectorRef
    ) {
        this.user = AuthService.getUser();
        this.cxc = data.cxc;
    }

    ngOnInit() {
        this.syncDisableClose(); // state = 'loading' → bloquear cierre desde el inicio
        this.transaction = moment().format('YYYYMMDDHHmmss') + this.cxc.cuenta_cobrar_id;
        this.registerConektaMessageListener();
        this.loadCheckout();
    }

    private registerConektaMessageListener(): void {
        this.messageHandler = (event: MessageEvent) => {
            if (!event.origin || !event.origin.includes('conekta.com')) { return; }
            const data = event.data;
            if (!data) { return; }
            const type: string = (data.type || data.event || data.action || '').toString().toLowerCase();
            if (type.includes('submit') || type.includes('pay') || type.includes('process')) {
                this.markPaymentSubmitted();
            }
        };
        window.addEventListener('message', this.messageHandler);
    }

    public openPolicy(): void {
        this.showPolicy = true;
    }

    public closePolicy(): void {
        this.showPolicy = false;
    }

    private markPaymentSubmitted(): void {
        if (this.paymentSubmitted) { return; }
        this.paymentSubmitted = true;
        this.syncDisableClose();
        this.cdr.detectChanges();
    }

    private syncDisableClose(): void {
        this.dialogRef.disableClose =
            this.state === 'loading' ||
            this.state === 'checkout' ||
            (this.state === 'confirm_subscription' && this.saving);
    }

    ngOnDestroy() {
        this.destroyed = true;
        if (this.messageHandler) {
            window.removeEventListener('message', this.messageHandler);
        }
        if (this.conektaScript && this.conektaScript.parentNode) {
            this.conektaScript.parentNode.removeChild(this.conektaScript);
        }
        // Limpiar la instancia cacheada para que el próximo open cargue
        // un componente fresco con el nuevo checkoutRequestId
        delete window.ConektaCheckoutComponents;
    }

    private loadCheckout() {
        const payload = {
            registration:      this.user.registration,
            business_id:       this.user.business_id,
            inst_id:           this.cxc.inst_id,
            amount:            this.cxc.monto,
            description:       this.cxc.descripcion + ' - ' + this.cxc.ciclo_id,
            concept_code:      this.data.concept,
            email:             this.user.email,
            name:              this.user.name + ' ' + this.user.lastname,
            transaction:       this.transaction,
            curp:              this.user.curp,
            type_payment:       'conekta_card',
            id_runway:        3,
            cuenta_cobrar_id:  this.cxc.cuenta_cobrar_id,
        };

        this.conektaService.createCheckoutSession(payload).subscribe(
            result => {
                if (result.success) {
                    this.checkoutId = result.checkout_id;
                    this.customerId = result.customer_id;
                    this.transaction = result.transaction; // usar la del backend (puede ser una preexistente)
                    this.state = 'checkout';
                    this.syncDisableClose();
                    this.cdr.detectChanges(); // fuerza render del *ngIf antes de buscar el div
                    this.initConektaComponent();
                } else {
                    this.errorMessage = result.message || 'Error al crear la sesión de pago.';
                    this.state = 'error';
                    this.syncDisableClose();
                }
            },
            err => {
                this.errorMessage = 'Domiciliación no disponible.';
                this.state = 'error';
                this.syncDisableClose();
            }
        );
    }

    private initConektaComponent() {
        // Cargar el script de Conekta si no está ya cargado
        if (window.ConektaCheckoutComponents) {
            setTimeout(() => this.renderConektaComponent(), 0);
            return;
        }

        this.conektaScript = document.createElement('script');
        this.conektaScript.src = 'https://pay.conekta.com/v1.0/js/conekta-checkout.min.js';
        this.conektaScript.onload = () => this.renderConektaComponent();
        this.conektaScript.onerror = () => {
            this.errorMessage = 'No se pudo cargar el componente de pago.';
            this.state = 'error';
            this.syncDisableClose();
        };
        document.body.appendChild(this.conektaScript);
    }

    private renderConektaComponent() {
        const config = {
            locale: 'es',
            publicKey: environment.conektaPublicKey,
            targetIFrame: '#conektaIframeContainer',
            checkoutRequestId: this.checkoutId,
        };
        const callbacks = {
            onGetInfoSuccess: (loadingTime: any) => {
                // Component loaded correctly
                console.log('loading time en milisegundos', loadingTime.initLoadTime);
            },
            onFinalizePayment: (order: any) => {
                if (this.destroyed) { return; }
                console.log('Pago aprobado, orden:', order);
                this.orderId = order.id;
                this.markPaymentSubmitted(); // respaldo si postMessage no lo detectó antes
                this.confirmApprovedPayment();
            },
            onErrorPayment: (error: any) => {
                console.error('Conekta error:', error);
                this.errorMessage = 'Tu pago fue declinado o no pudo procesarse.';
                this.state = 'error';
                this.syncDisableClose();
                // best-effort: registrar el intento fallido
                this.conektaService.confirmPayment({
                    transaction: this.transaction,
                    order_id: null,
                    status: 'DECLINADA'
                }).subscribe();
            }
        };
        const options = {
            backgroundMode: 'lightMode',
            colorPrimary: '#081133',
            colorText: '#585987',
            colorLabel: '#585987',
            inputType: 'minimalMode',
        };
        window.ConektaCheckoutComponents.Integration({ config, callbacks, options });
    }

    private confirmApprovedPayment() {
        this.conektaService.confirmPayment({
            transaction: this.transaction,
            order_id:    this.orderId,
            status:      'APROBADA'
        }).subscribe(
            result => {
                if (!result.success) {
                    this.errorMessage = result.message || 'Error al registrar el pago. Por favor contacta a soporte.';
                    this.state = 'error';
                    this.syncDisableClose();
                    return;
                }
                // Correo de confirmación (best-effort)
                this.ticketService.getEmailsCC().subscribe(
                    emails => {
                        const mailData = {
                            to:         this.user.email,
                            subject:    'CNCI Virtual: Pago aprobado',
                            message:    this.buildEmailBody(),
                            nameTo:     this.user.name,
                            nameFrom:   'CNCI Virtual',
                            image:      0,
                            logo:       this.user.business_id === 3 ? 'logocnci.jpg' : 'logocnci_propios.jpg',
                            who:        'studentportal',
                            recipients: JSON.stringify(emails)
                        };
                        this.ticketService.sendMail(mailData).subscribe(
                            mailResult => {
                                if (mailResult && mailResult['error']) {
                                    this.toastr.error('Ocurrió un error al enviar el correo de confirmación.');
                                }
                            },
                            () => { /* best-effort */ }
                        );
                    },
                    () => { /* best-effort */ }
                );
                if (this.data.hasActiveSubscription) {
                    this.state = 'success';
                    this.syncDisableClose();
                    setTimeout(() => this.dialogRef.close(true), 1500);
                } else {
                    this.state = 'confirm_subscription';
                    this.syncDisableClose();
                }
            },
            err => {
                console.error('confirmPayment error:', err);
                this.errorMessage = 'Error al registrar el pago. Por favor contacta a soporte.';
                this.state = 'error';
                this.syncDisableClose();
            }
        );
    }

    confirmSubscription(wantSubscription: boolean) {
        if (!wantSubscription) {
            this.dialogRef.close(true);
            return;
        }
        if (this.saving) { return; }
        this.saving = true;
        this.syncDisableClose();

        const payload = {
            registration:        this.user.registration,
            business_id:         this.user.business_id,
            inst_id:             this.cxc.inst_id,
            concept_code:        this.data.concept,
            conekta_customer_id: this.customerId,
            order_id:            this.orderId,
            transaction:         this.transaction,
            concept_description:        this.data.concept_description,
        };

        this.conektaService.saveSubscription(payload).subscribe(
            result => {
                if (result.success) {
                    this.state = 'success';
                    setTimeout(() => this.dialogRef.close(true), 1500);
                } else {
                    this.saving = false;
                    this.syncDisableClose();
                    this.toastr.error(result.message || 'Error al guardar la suscripción.');
                }
            },
            err => {
                this.saving = false;
                this.syncDisableClose();
                this.toastr.error('No se pudo guardar la domiciliación.');
            }
        );
    }

    private buildEmailBody(): string {
        return `<p>Tu pago de <b>${this.cxc.descripcion} - ${this.cxc.ciclo_id}</b> ` +
               `por <b>$${this.cxc.monto} MXN</b> ha sido aprobado. ` +
               `Número de orden: <b>${this.orderId}</b></p>`;
    }
}
